// SupervisorTaskList.tsx
import React, { useState, useMemo } from 'react';
import { SupervisionCadenceDto } from '../../../../services/weeklySupervision/weeklySupervisionService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../firebase/AuthProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Flag } from 'lucide-react';
import moment from 'moment';

interface SupervisorTaskListProps {
    taskList: any[];
    selectedSC: SupervisionCadenceDto;
    handleEditComment: (index: number) => void;
    isLoading: boolean
}

const SupervisorTaskList: React.FC<SupervisorTaskListProps> = ({
    taskList,
    selectedSC,
    handleEditComment,
    isLoading,
}) => {
    const { currentOrganization } = useAuth();
    const navigate = useNavigate();

    // Grouping State
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

    // Group Data by Participant
    const groupedData = useMemo(() => {
        return taskList.reduce((groups: Record<string, any[]>, task) => {
            const participantName = task.participant
                ? `${task.participant.firstName} ${task.participant.lastName}`
                : "Unassigned / General";

            if (!groups[participantName]) {
                groups[participantName] = [];
            }
            groups[participantName].push(task);
            return groups;
        }, {});
    }, [taskList]);

    const toggleGroup = (groupName: string) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    // Helper to find original index for editing
    const handleGroupedEditClick = (task: any) => {
        // Find the index in the original flat taskList array to pass back to parent
        const originalIndex = taskList.findIndex(t => t.id === task.id);
        if (originalIndex !== -1) {
            handleEditComment(originalIndex);
        }
    };

    // Calculate days late
    const calculateDaysLate = (endDate: string) => {
        if (!endDate) return 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(endDate);
        dueDate.setHours(0, 0, 0, 0);
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays < 0 ? Math.abs(diffDays) : 0;
    };

    // Get SubType Badge Class Name
    const getSubTypeBadgeClassName = (subTypeName: string | undefined) => {
        const baseClass = "subtype-badge";
        if (!subTypeName) return `${baseClass} subtype-badge-default`;
        
        const normalizedSubType = subTypeName.toUpperCase().trim();

        switch (normalizedSubType) {
            case "MONTHLY":
                return `${baseClass} subtype-badge-monthly`;
            case "QUARTERLY":
                return `${baseClass} subtype-badge-quarterly`;
            case "ANNUAL":
                return `${baseClass} subtype-badge-annual`;
            case "INITIAL":
                return `${baseClass} subtype-badge-initial`;
            case "INTERIM":
                return `${baseClass} subtype-badge-interim`;
            default:
                return `${baseClass} subtype-badge-default`;
        }
    };

    // Get Status Badge Class Name
    const getStatusBadgeClassName = (statusName: string) => {
        const baseClass = "badge-status-custom1 ";
        const normalizedStatus = statusName.toUpperCase().trim();

        switch (normalizedStatus) {
            case "NOT STARTED":
                return `${baseClass} badge-status-custom-not-started`;
            case "IN REVIEW (SUP)":
                return `${baseClass} badge-status-custom-in-review-sup`;
            case "IN REVIEW (PM)":
            case "IN REVIEW":
                return `${baseClass} badge-status-custom-in-review`;
            case "COMPLETED":
                return `${baseClass} badge-status-custom-completed`;
            case "PENDING EXTENSION":
                return `${baseClass} badge-status-custom-pending-extension`;
            case "EXTENDED":
                return `${baseClass} badge-status-custom-extended`;
            case "READY_":
            case "READY":
                return `${baseClass} badge-status-custom-ready`;
            case "ISSUES FOUND":
                return `${baseClass} badge-status-custom-issues`;
            case "CLOSED":
                return `${baseClass} badge-status-custom-closed`;
            default:
                return `${baseClass} badge-status-custom-default`;
        }
    };

    return (
        <>
            <div className="weekly-supervision-list">
                {isLoading ? (
                    <div className="weekly-supervision-empty">Loading...</div>
                ) : Object.keys(groupedData).length === 0 ? (
                    <div className="weekly-supervision-empty">No tasks found for this period.</div>
                ) : (
                    Object.keys(groupedData).map((groupName) => {
                        const isCollapsed = collapsedGroups[groupName];
                        const taskCount = groupedData[groupName].length;

                        return (
                            <div key={groupName} className="weekly-supervision-group">
                                {/* GROUP HEADER CARD */}
                                <div
                                    className="weekly-supervision-group-header"
                                    onClick={() => toggleGroup(groupName)}
                                >
                                    <span className="group-name">{groupName}</span>
                                    <div className="group-actions">
                                        <button className="task-count-btn">
                                            {taskCount} Tasks
                                        </button>
                                        <FontAwesomeIcon
                                            icon={isCollapsed ? faChevronRight : faChevronDown}
                                            className="chevron-icon"
                                        />
                                    </div>
                                </div>

                                {/* TASK CARDS */}
                                {!isCollapsed && groupedData[groupName].map((record: any) => {
                                    const daysLate = calculateDaysLate(record.endDate);
                                    const taskId = record.id;
                                    const orgAbbr = currentOrganization?.abbreviation || '';
                                    const fullTaskId = `${orgAbbr}-${taskId}`;
                                    const subTypeValue = record.subType?.name || record.subType?.type || record.subType || 'Monthly';
                                    const taskStatusName = record.taskStatus?.name || record.status?.name || '';
                                    const scComment = record.scComments?.[0]?.comment || '';
                                    const supervisorComment = record.supervisorComments?.[0]?.comment || '';

                                    return (
                                        <div
                                            key={record.id}
                                            className="weekly-supervision-task-card"
                                            onClick={() => navigate(`/task/${fullTaskId}`)}
                                        >
                                            <div className="task-card-left">
                                                <div className="task-id-name">
                                                    {fullTaskId}
                                                </div>
                                                <div className="task-title">{record.title}</div>
                                                {scComment && (
                                                    <div style={{ fontSize: '0.875rem', color: '#747474', marginTop: '0.5rem' }}>
                                                        SC - {scComment}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="task-card-middle">
                                                {daysLate > 0 && (
                                                    <span className="late-indicator">Late: {daysLate} days</span>
                                                )}
                                            </div>

                                            <div className="task-card-right">
                                                {/* SubType Badge */}
                                                <button className={getSubTypeBadgeClassName(subTypeValue)}>
                                                    {subTypeValue}
                                                </button>

                                                {/* Task Status Badge */}
                                                {taskStatusName && (
                                                    <span
                                                        className={`${getStatusBadgeClassName(
                                                            taskStatusName
                                                        )} ps-task-status-new gap-1 custom-badge rounded-1`}
                                                    >
                                                        <Flag size={12} />{" "}
                                                        {taskStatusName === "READY_"
                                                            ? "READY"
                                                            : taskStatusName}
                                                    </span>
                                                )}

                                                {/* Comment Section */}
                                                <div
                                                    className="comment-section"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleGroupedEditClick(record);
                                                    }}
                                                >
                                                    <span className={`comment-text ${!supervisorComment ? 'comment-placeholder' : ''}`}>
                                                        {supervisorComment || "Add a comment...."}
                                                    </span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <g clipPath="url(#clip0_3546_18737)">
                                                            <path d="M14.1147 4.54126C14.4671 4.18888 14.6652 3.71091 14.6652 3.2125C14.6653 2.71409 14.4674 2.23607 14.115 1.8836C13.7626 1.53112 13.2846 1.33307 12.7862 1.33301C12.2878 1.33295 11.8098 1.53088 11.4573 1.88326L2.55999 10.7826C2.4052 10.9369 2.29073 11.127 2.22665 11.3359L1.34599 14.2373C1.32876 14.2949 1.32746 14.3562 1.34222 14.4145C1.35699 14.4728 1.38727 14.5261 1.42985 14.5686C1.47244 14.6111 1.52573 14.6413 1.58409 14.656C1.64245 14.6707 1.70369 14.6693 1.76132 14.6519L4.66332 13.7719C4.8721 13.7084 5.0621 13.5947 5.21665 13.4406L14.1147 4.54126Z" stroke="#7E57C2" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                        </g>
                                                        <defs>
                                                            <clipPath id="clip0_3546_18737">
                                                                <rect width="16" height="16" fill="white" />
                                                            </clipPath>
                                                        </defs>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
};

export default SupervisorTaskList;